import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, GraduationCap, Calendar, Clock, CheckCircle2, PlayCircle, StopCircle, AlertTriangle, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { formatReceivedAt } from "../lib/dateFormat";
import PortalMessages from "../components/portal/PortalMessages";
import HomeworkPanel from "../components/portal/HomeworkPanel";
import StudyMaterialsPanel from "../components/portal/StudyMaterialsPanel";

/**
 * V157 — Public Parent / Tutor portal.
 *
 * Route: /portal/parent/:token or /portal/tutor/:token
 * Bearer-token authentication (Q3 = link only for MVP). Token is the last
 * path segment — SHA-256'd on the backend before lookup. Revoked / expired
 * tokens return 410; the page renders a friendly "Please request a new link"
 * empty state so parents don't panic.
 *
 * Mobile-first single-column layout. Fully privacy-safe — parent view never
 * shows tutor contact and tutor view never shows parent contact (server-side
 * `_project_for_party` guarantees this).
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Unauthenticated axios (portal endpoints don't use JWT)
const publicApi = axios.create({ baseURL: API });

const STATUS_TONES = {
    confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
    proposed: "bg-amber-50 text-amber-700 border-amber-200",
    awaiting_confirmation: "bg-amber-50 text-amber-700 border-amber-200",
    rescheduled: "bg-blue-50 text-blue-700 border-blue-200",
    cancelled: "bg-rose-50 text-rose-700 border-rose-200",
    completed: "bg-slate-100 text-slate-700 border-slate-200",
    no_show: "bg-rose-50 text-rose-700 border-rose-200",
};

export default function PortalPage({ party }) {
    const { token } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [pendingAction, setPendingAction] = useState(null);

    const reload = useCallback(async () => {
        try {
            const r = await publicApi.get(`/portal/${party}/${token}`);
            setData(r.data);
            setErr(null);
        } catch (e) {
            const status = e?.response?.status;
            if (status === 410) setErr("This link has been revoked or expired. Please request a new one from Anand Tutorials.");
            else if (status === 404) setErr("This link is invalid. Please double-check the link you received.");
            else setErr("Unable to load. Please retry in a moment.");
        } finally {
            setLoading(false);
        }
    }, [party, token]);

    useEffect(() => { reload(); }, [reload]);

    // V157 — Strip any pre-rendered JSON-LD / marketing meta from <head> so a
    // secure portal never carries the public site's Organization / LocalBusiness
    // schema. The hardcoded blocks in public/index.html render BEFORE React
    // boots, so a route-aware React wrapper alone isn't enough.
    useEffect(() => {
        const scripts = document.querySelectorAll('script[type="application/ld+json"]');
        const removed = [];
        scripts.forEach((s) => {
            const txt = s.textContent || "";
            if (/Organization|LocalBusiness|EducationalOrganization|WebSite/.test(txt)) {
                removed.push(s);
                s.parentNode?.removeChild(s);
            }
        });
        // Swap any existing marketing robots meta with a noindex one so
        // accidentally-shared tokens are never crawled. Restore on unmount.
        const existingRobots = document.querySelector('meta[name="robots"]');
        const priorContent = existingRobots?.getAttribute("content") || null;
        let injected = null;
        if (existingRobots) {
            existingRobots.setAttribute("content", "noindex, nofollow");
        } else {
            injected = document.createElement("meta");
            injected.name = "robots";
            injected.content = "noindex, nofollow";
            document.head.appendChild(injected);
        }
        return () => {
            removed.forEach((s) => document.head.appendChild(s));
            if (existingRobots && priorContent !== null) existingRobots.setAttribute("content", priorContent);
            injected?.parentNode?.removeChild(injected);
        };
    }, []);

    const joinClass = async (sched) => {
        // V167 — Provider fallback. If admin pasted a meeting_url (Zoom /
        // Google Meet / Teams / Jitsi) on the schedule, open it in a new
        // tab and log a "portal_join_click" so attendance still gets
        // recorded. Otherwise fall through to the native 100ms classroom.
        const url = (sched?.meeting_url || "").trim();
        if (url) {
            try {
                // Best-effort: still call session/start so the server timeline
                // has a Join-clicked event (drives V162 reminders / admin
                // presence). Failure is non-fatal — the meeting must still open.
                await publicApi.post(`/portal/${party}/${token}/session/${sched.id}/start`).catch(() => null);
            } catch (_e) { /* noop */ }
            window.open(url, "_blank", "noopener,noreferrer");
            toast.success("Opening meeting…");
            return;
        }
        // Default — native in-platform live classroom (100ms). Parents &
        // tutors never leave the Anand Tutorials domain — attendance &
        // timing stay authoritative.
        navigate(`/portal/${party}/${token}/session/${sched.id}/live`);
    };

    const endClass = async (sched) => {
        setPendingAction(`end-${sched.id}`);
        try {
            await publicApi.post(`/portal/${party}/${token}/session/${sched.id}/end`);
            toast.success("Session ended. Please complete the class report.");
            reload();
        } catch (_e) {
            toast.error("Could not end the class.");
        } finally {
            setPendingAction(null);
        }
    };

    if (loading) return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
        </div>
    );
    if (err || !data) return <PortalError message={err || "Something went wrong."} />;

    const a = data.assignment || {};
    const schedules = data.schedules || [];
    const sessions = data.sessions || [];
    const now = new Date();
    const upcoming = schedules.filter((s) => {
        try { return new Date(s.scheduled_end) >= now && s.status !== "cancelled"; }
        catch { return false; }
    });
    const past = schedules.filter((s) => !upcoming.includes(s));
    const nextClass = upcoming[0];

    const isTutor = party === "tutor";
    const title = isTutor ? "Tutor Portal" : "Parent Portal";
    const subtitle = isTutor
        ? `Assigned student · ${a.student_name || "—"}`
        : `Your child's classroom · ${a.student_name || "—"}`;
    const sessionRecordFor = (sid) => sessions.find((r) => r.schedule_id === sid);
    const attendance = data.attendance_summary || { present: 0, total_recorded: 0 };
    const totalSessions = a.total_sessions || 0;
    const completed = a.completed_sessions || 0;
    const remaining = Math.max(0, totalSessions - completed);
    const pct = totalSessions ? Math.min(100, Math.round((completed / totalSessions) * 100)) : 0;

    return (
        <div className="min-h-screen bg-slate-50 classroom-mobile" data-testid={`portal-page-${party}`}>
            <header className="bg-[#0F172A] text-white px-4 sm:px-6 py-5">
                <div className="max-w-3xl mx-auto">
                    <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-bold">Anand Tutorials</div>
                    <h1 className="text-lg sm:text-2xl font-heading font-bold mt-1">{title}</h1>
                    <p className="text-xs sm:text-sm text-slate-300 mt-0.5">{subtitle}</p>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-4 sm:px-6 py-5 space-y-5">
                {/* Overview cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <Stat label="Total sessions" value={totalSessions || "—"} />
                    <Stat label="Completed" value={completed} tone="text-emerald-700" />
                    <Stat label="Remaining" value={remaining} tone="text-blue-700" />
                    <Stat label="Attendance" value={attendance.total_recorded ? `${attendance.present}/${attendance.total_recorded}` : "—"} />
                </div>
                {totalSessions > 0 && (
                    <div>
                        <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                            <span>Package progress</span>
                            <span className="font-semibold">{pct}%</span>
                        </div>
                        <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
                        </div>
                    </div>
                )}

                {/* Next class */}
                {nextClass && (
                    <NextClassCard
                        schedule={nextClass}
                        onJoin={() => joinClass(nextClass)}
                        onEnd={() => endClass(nextClass)}
                        joining={pendingAction === `join-${nextClass.id}`}
                        ending={pendingAction === `end-${nextClass.id}`}
                        isTutor={isTutor}
                        record={sessionRecordFor(nextClass.id)}
                    />
                )}

                {/* Upcoming schedule */}
                <Section title={isTutor ? "My schedule" : "Upcoming classes"}>
                    {upcoming.length === 0 ? (
                        <EmptyLine>No upcoming classes yet. Anand Tutorials will publish your schedule shortly.</EmptyLine>
                    ) : upcoming.map((s) => (
                        <SessionRow key={s.id} schedule={s} onJoin={() => joinClass(s)} isTutor={isTutor} record={sessionRecordFor(s.id)} pending={pendingAction === `join-${s.id}`} />
                    ))}
                </Section>

                {/* Past sessions */}
                <Section title="Session history">
                    {past.length === 0 ? (
                        <EmptyLine>No completed sessions yet.</EmptyLine>
                    ) : past.map((s) => {
                        const rec = sessionRecordFor(s.id);
                        return (
                            <div key={s.id} className="border border-slate-200 rounded-xl p-3 bg-white flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="text-sm font-semibold text-[#0F172A]">Session #{s.session_number}</div>
                                    <div className="text-xs text-slate-500 mt-0.5">{formatReceivedAt(s.scheduled_start)}</div>
                                    {rec?.topic_covered && <div className="text-xs text-slate-700 mt-1"><span className="text-slate-400">Topic:</span> {rec.topic_covered}</div>}
                                    {rec?.homework && <div className="text-xs text-slate-700"><span className="text-slate-400">Homework:</span> {rec.homework}</div>}
                                    {rec?.tutor_remarks && !isTutor && <div className="text-xs text-slate-700 italic mt-1">"{rec.tutor_remarks}"</div>}
                                    {rec?.whiteboard_snapshot_data_url && (
                                        <a
                                            href={rec.whiteboard_snapshot_data_url}
                                            target="_blank"
                                            rel="noreferrer"
                                            data-testid="portal-whiteboard-snapshot"
                                            className="mt-2 block rounded-lg overflow-hidden border border-slate-200 hover:border-cyan-400 transition-colors"
                                        >
                                            <img src={rec.whiteboard_snapshot_data_url} alt="Whiteboard snapshot" className="w-full max-w-[240px] h-auto" />
                                            <div className="text-[10px] text-slate-500 px-1.5 py-1 bg-slate-50">Whiteboard · tap to enlarge</div>
                                        </a>
                                    )}
                                    {rec?.elapsed_seconds ? (
                                        <div className="text-[11px] text-slate-500 mt-1">
                                            <Clock className="w-3 h-3 inline mr-0.5" />
                                            Duration: {Math.round(rec.elapsed_seconds / 60)} min
                                        </div>
                                    ) : null}
                                </div>
                                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border shrink-0 ${STATUS_TONES[s.status] || STATUS_TONES.completed}`}>
                                    {s.status.replace(/_/g, " ")}
                                </span>
                            </div>
                        );
                    })}
                </Section>

                {/* Class info */}
                <Section title="Class information">
                    <InfoGrid>
                        <Info label="Subject" value={a.subject} />
                        <Info label="Grade" value={a.grade} />
                        <Info label="Board" value={a.board} />
                        <Info label="Class mode" value={a.class_mode} />
                        {isTutor ? (
                            <>
                                <Info label="Student" value={a.student_name} />
                                <Info label="City" value={a.city} />
                            </>
                        ) : (
                            <>
                                <Info label="Tutor" value={a.tutor_name} />
                                <Info label="Qualification" value={a.tutor_qualification} />
                                <Info label="Experience" value={a.tutor_experience_years ? `${a.tutor_experience_years} years` : ""} />
                            </>
                        )}
                    </InfoGrid>
                </Section>

                {/* V160 — Messaging */}
                <PortalMessages party={party} token={token} assignmentId={a.id} />

                {/* V165 — Homework */}
                <HomeworkPanel party={party} token={token} />

                {/* V166 — Study Materials Library */}
                <StudyMaterialsPanel party={party} token={token} />

                {/* Contact support (parent) */}
                {!isTutor && (
                    <div className="bg-white border border-slate-200 rounded-2xl p-4 text-center">
                        <div className="text-sm font-semibold text-[#0F172A]">Need help?</div>
                        <p className="text-xs text-slate-500 mt-1">Anand Tutorials handles all coordination for you. Please contact us for any scheduling or class issue — do not share personal contact details.</p>
                        <a href="mailto:support@anandtutorials.com" className="inline-flex items-center gap-1.5 mt-3 text-cyan-700 font-semibold text-sm hover:underline">
                            <MessageSquare className="w-4 h-4" />
                            Contact Anand Tutorials
                        </a>
                    </div>
                )}
            </main>

            <footer className="max-w-3xl mx-auto px-4 sm:px-6 py-6 text-center text-[11px] text-slate-400">
                © 2026 Anand Tutorials · Secure portal · This link is private to you and must not be shared.
            </footer>
        </div>
    );
}

function Stat({ label, value, tone = "text-slate-800" }) {
    return (
        <div className="bg-white border border-slate-200 rounded-xl p-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{label}</div>
            <div className={`text-lg font-heading font-bold mt-0.5 ${tone}`}>{value}</div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <section>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-2">{title}</div>
            <div className="space-y-2">{children}</div>
        </section>
    );
}

function SessionRow({ schedule, onJoin, isTutor, record, pending }) {
    return (
        <div className="border border-slate-200 rounded-xl p-3 bg-white flex items-start justify-between gap-3" data-testid={`portal-session-${schedule.session_number}`}>
            <div className="min-w-0">
                <div className="text-sm font-semibold text-[#0F172A]">Session #{schedule.session_number}</div>
                <div className="text-xs text-slate-500 mt-0.5">{formatReceivedAt(schedule.scheduled_start)}</div>
                <div className="text-[11px] text-slate-400 mt-0.5">Timezone · {schedule.timezone}</div>
            </div>
            <div className="flex flex-col items-end gap-2 shrink-0">
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${STATUS_TONES[schedule.status] || STATUS_TONES.confirmed}`}>
                    {schedule.status.replace(/_/g, " ")}
                </span>
                {schedule.status !== "completed" && schedule.status !== "cancelled" && (
                    <button
                        onClick={onJoin}
                        disabled={pending}
                        data-testid={`portal-session-join-${schedule.session_number}`}
                        data-cm-primary
                        className="inline-flex items-center gap-1.5 bg-emerald-600 text-white px-3.5 py-2 rounded-lg text-xs sm:text-[11px] font-semibold hover:bg-emerald-700 disabled:opacity-60"
                    >
                        {pending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />}
                        Join
                    </button>
                )}
                {isTutor && record?.completion_status === "completed" && (
                    <span className="text-[10px] text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Report submitted</span>
                )}
            </div>
        </div>
    );
}

function InfoGrid({ children }) {
    return <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 bg-white border border-slate-200 rounded-xl p-3">{children}</div>;
}

// V162 — Next Class card with a live "starting in Xm" pill + emphasised Join
// button when the class is within 30 min of its scheduled start. Updates
// once per minute; no wall-clock guarantees needed for this UX.
function NextClassCard({ schedule, onJoin, onEnd, joining, ending, isTutor, record }) {
    const [tick, setTick] = useState(0);
    useEffect(() => {
        const iv = setInterval(() => setTick((t) => t + 1), 60000);
        return () => clearInterval(iv);
    }, []);
    void tick; // subscribe to re-render; value not read directly

    const start = new Date(schedule.scheduled_start);
    const minsOut = Math.max(0, Math.floor((start.getTime() - Date.now()) / 60000));
    const startingSoon = minsOut <= 30;
    const inSession = record?.completion_status === "in_progress";
    const canEndClass = isTutor && inSession;

    return (
        <div
            className={`rounded-2xl p-4 shadow-sm transition-colors ${
                startingSoon
                    ? "bg-gradient-to-br from-cyan-50 to-emerald-50 border-2 border-cyan-400"
                    : "bg-white border border-cyan-200"
            }`}
            data-testid="portal-next-class"
        >
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-[10px] uppercase tracking-widest text-cyan-700 font-bold">Next class</div>
                {startingSoon && (
                    <span
                        data-testid="portal-starting-soon-pill"
                        className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${
                            minsOut <= 5
                                ? "bg-rose-500 text-white animate-pulse"
                                : "bg-emerald-500 text-white"
                        }`}
                    >
                        <Clock className="w-3 h-3" />
                        {minsOut <= 1 ? "Starting now" : `Starting in ${minsOut} min`}
                    </span>
                )}
            </div>
            <div className="flex items-start justify-between gap-3 mt-1">
                <div className="min-w-0">
                    <div className="text-lg font-heading font-bold text-[#0F172A]">Session #{schedule.session_number}</div>
                    <div className="text-sm text-slate-600 mt-0.5">
                        <Calendar className="w-3.5 h-3.5 inline mr-1 text-slate-400" />
                        {formatReceivedAt(schedule.scheduled_start)}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">Timezone · {schedule.timezone}</div>
                </div>
                <button
                    onClick={onJoin}
                    disabled={joining}
                    data-testid="portal-join-class"
                    data-cm-primary
                    className={`inline-flex items-center gap-1.5 text-white px-4 py-2 rounded-lg font-semibold text-sm shadow-sm disabled:opacity-60 ${
                        startingSoon
                            ? "bg-emerald-600 hover:bg-emerald-700 ring-2 ring-emerald-300"
                            : "bg-emerald-600 hover:bg-emerald-700"
                    }`}
                >
                    {joining ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
                    Join Class
                </button>
            </div>
            {canEndClass && (
                <button
                    onClick={onEnd}
                    disabled={ending}
                    data-testid="portal-end-class"
                    data-cm-primary
                    className="mt-3 w-full inline-flex items-center justify-center gap-1.5 bg-slate-800 text-white px-4 py-2 rounded-lg font-semibold text-sm hover:bg-slate-900 disabled:opacity-60"
                >
                    {ending ? <Loader2 className="w-4 h-4 animate-spin" /> : <StopCircle className="w-4 h-4" />}
                    End Class
                </button>
            )}
        </div>
    );
}


function Info({ label, value }) {
    return (
        <div>
            <div className="text-[10px] uppercase text-slate-400 font-semibold">{label}</div>
            <div className="text-sm text-slate-800 mt-0.5">{value || <span className="text-slate-300">—</span>}</div>
        </div>
    );
}

function EmptyLine({ children }) {
    return <div className="text-xs text-slate-500 bg-white border border-dashed border-slate-200 rounded-xl px-3 py-4 text-center">{children}</div>;
}

function PortalError({ message }) {
    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
            <div className="max-w-md w-full text-center bg-white border border-rose-200 rounded-2xl p-6 shadow-sm">
                <AlertTriangle className="w-8 h-8 mx-auto text-rose-500 mb-2" />
                <h1 className="text-lg font-heading font-bold text-[#0F172A]">Link unavailable</h1>
                <p className="text-sm text-slate-600 mt-2">{message}</p>
                <a href="mailto:support@anandtutorials.com" className="inline-block mt-4 text-cyan-700 font-semibold text-sm hover:underline">
                    Contact Anand Tutorials
                </a>
            </div>
        </div>
    );
}
